import { format } from 'date-fns'
import { de } from 'date-fns/locale'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { formatDateTime } from '@/lib/date'
import { formatCourseCredits } from '@/lib/subscriptions'
import type { PublicSession } from '@/types/domain'

type PublicSessionCardProps = {
  session: PublicSession
  isCheckingExistingBookings: boolean
  hasExistingBooking: boolean
  showFullTrainerName: boolean
  onBook: (session: PublicSession) => void
  onCancel?: (session: PublicSession) => void
}

export function PublicSessionCard({
  session,
  isCheckingExistingBookings,
  hasExistingBooking,
  showFullTrainerName,
  onBook,
  onCancel,
}: PublicSessionCardProps) {
  const isFullyBooked = session.available_spots <= 0
  const isSessionStarted = new Date(session.starts_at) <= new Date()
  const isRegistrationClosed = session.registration_until
    ? new Date(session.registration_until) <= new Date()
    : false
  const bookingDisabled =
    isCheckingExistingBookings ||
    isFullyBooked ||
    hasExistingBooking ||
    isSessionStarted ||
    isRegistrationClosed
  const isMinParticipantsReached =
    session.min_participants !== null &&
    session.confirmed_count >= session.min_participants

  return (
    <Card
      className={`border-border/80 transition-colors ${
        isMinParticipantsReached ? 'border-emerald-200 bg-emerald-50/30' : ''
      }`}
    >
      <CardContent className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Badge variant={isFullyBooked ? 'destructive' : 'default'}>
              {isFullyBooked ? 'Ausgebucht' : `${session.available_spots} Plätze frei`}
            </Badge>
            {hasExistingBooking && <Badge variant="outline">Bereits gebucht</Badge>}
            {isMinParticipantsReached && (
              <Badge className="bg-emerald-600 text-white hover:bg-emerald-700">
                Stattfinden garantiert
              </Badge>
            )}
          </div>

          <p className="text-sm font-medium text-foreground">
            {format(new Date(session.starts_at), 'EEEE, dd. MMMM yyyy', { locale: de })} -{' '}
            {format(new Date(session.starts_at), 'HH:mm')} bis{' '}
            {format(new Date(session.ends_at), 'HH:mm')} Uhr
          </p>
          <p className="text-sm text-muted-foreground">
            Anmeldung möglich bis: {formatDateTime(session.registration_until)}
          </p>
          <p className="text-sm text-muted-foreground">
            Stornierung möglich bis: {formatDateTime(session.cancellation_until)}
          </p>
          <p className="text-sm text-muted-foreground">
            Kurswert: <span className="font-medium">{formatCourseCredits(session.credits_per_session)}</span>
          </p>
          {session.trainer_name && (
            <p className="mt-1 text-sm text-muted-foreground">
              Trainer:{' '}
              <span className="font-medium">
                {showFullTrainerName ? session.trainer_name : session.trainer_name.split(' ')[0]}
              </span>
            </p>
          )}
          {session.resource_name && (
            <p className="mt-1 text-sm text-muted-foreground">
              Ort: <span className="font-medium">{session.resource_name}</span>
            </p>
          )}
        </div>

        <div className="flex w-full shrink-0 flex-col gap-2 md:w-60">
          {hasExistingBooking && !isCheckingExistingBookings && onCancel ? (
            <Button variant="outline" onClick={() => onCancel(session)}>
              Stornieren
            </Button>
          ) : bookingDisabled ? (
            <Button type="button" disabled>
              {isCheckingExistingBookings
                ? 'Prüfe ...'
                : hasExistingBooking
                  ? 'Bereits gebucht'
                  : isSessionStarted
                    ? 'Bereits gestartet'
                    : isRegistrationClosed
                      ? 'Anmeldung geschlossen'
                      : 'Ausgebucht'}
            </Button>
          ) : (
            <Button onClick={() => onBook(session)}>Jetzt buchen</Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
